import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';

interface NavItem {
  icon: string;
  label: string;
  path?: string;
  badge?: string;
  badgeType?: 'new' | 'trial' | 'live' | 'hot' | 'count';
  locked?: boolean;
  count?: number;
}

interface NavSection {
  title?: string;
  items: NavItem[];
}

const OWNER_NAV: NavSection[] = [
  {
    items: [
      { icon: 'ri-vip-crown-line', label: 'nav_owner_home', path: '/dashboard/owner' },
    ],
  },
  {
    title: 'nav_gen',
    items: [
      { icon: 'ri-bar-chart-2-line', label: 'nav_forecast', path: '/forecast', badge: 'LIVE', badgeType: 'live' },
      { icon: 'ri-database-2-line', label: 'nav_disp_fact', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-git-commit-line', label: 'nav_gen_deviation', locked: true, badge: 'скоро', badgeType: 'new' },
    ],
  },
  {
    title: 'nav_bess',
    items: [
      { icon: 'ri-battery-2-charge-line', label: 'nav_bess_state', path: '/bess', badge: 'LIVE', badgeType: 'live' },
    ],
  },
  {
    title: 'nav_sec_finance',
    items: [
      { icon: 'ri-money-dollar-circle-line', label: 'nav_fin_income', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-calculator-line', label: 'nav_fin_charges', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-battery-share-line', label: 'nav_fin_bess_arb', locked: true, badge: 'скоро', badgeType: 'new' },
    ],
  },
  {
    title: 'nav_sec_analytics',
    items: [
      { icon: 'ri-exchange-dollar-line', label: 'nav_an_market', path: '/market/ueex' },
      { icon: 'ri-newspaper-line', label: 'nav_an_news', path: '/info', badge: 'AI', badgeType: 'live' },
    ],
  },
  {
    items: [
      { icon: 'ri-notification-3-line', label: 'nav_notifications', locked: true, badge: 'скоро', badgeType: 'new' },
    ],
  },
];

const FULL_NAV: NavSection[] = [
  {
    items: [
      { icon: 'ri-home-4-line', label: 'nav_dashboard', path: '/' },
    ],
  },
  {
    title: 'nav_gen',
    items: [
      { icon: 'ri-bar-chart-2-line', label: 'nav_forecast', path: '/forecast', badge: 'LIVE', badgeType: 'live' },
      { icon: 'ri-database-2-line', label: 'nav_disp_fact', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-git-commit-line', label: 'nav_gen_deviation', locked: true, badge: 'скоро', badgeType: 'new' },
    ],
  },
  {
    title: 'nav_sec_market',
    items: [
      { icon: 'ri-funds-line', label: 'nav_market_prices', path: '/market/prices' },
    ],
  },
  {
    title: 'nav_dispatch2',
    items: [
      { icon: 'ri-line-chart-line', label: 'nav_trade_schedule', path: '/trading' },
      { icon: 'ri-send-plane-line', label: 'nav_mms', path: '/dashboard/owner/comdisp' },
      { icon: 'ri-terminal-box-line', label: 'nav_disp_commands', path: '/sko' },
    ],
  },
  {
    title: 'nav_disp_scada',
    items: [
      { icon: 'ri-cpu-line', label: 'nav_scada_monitoring', path: '/scada/dispatch', badge: 'PREVIEW', badgeType: 'new' },
      { icon: 'ri-router-line', label: 'nav_scada_telemetry', path: '/scada/emsbox' },
      { icon: 'ri-equalizer-line', label: 'nav_scada_control', path: '/scada/control' },
      { icon: 'ri-history-line', label: 'nav_scada_events', path: '/scada/events' },
    ],
  },
  {
    title: 'nav_bess',
    items: [
      { icon: 'ri-battery-2-charge-line', label: 'nav_bess_state', path: '/bess', badge: 'LIVE', badgeType: 'live' },
      { icon: 'ri-battery-charge-line', label: 'nav_bess_charge', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-flashlight-line', label: 'nav_bess_discharge', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-swap-box-line', label: 'nav_bess_arbitrage', locked: true, badge: 'скоро', badgeType: 'new' },
    ],
  },
  {
    title: 'nav_assets',
    items: [
      { icon: 'ri-flashlight-line', label: 'nav_assets_list', path: '/assets' },
    ],
  },
  {
    title: 'nav_incidents',
    items: [
      { icon: 'ri-alarm-warning-line', label: 'nav_inc_faults', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-forbid-2-line', label: 'nav_inc_limits', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-notification-badge-line', label: 'nav_inc_alarms', locked: true, badge: 'скоро', badgeType: 'new' },
    ],
  },
  {
    title: 'nav_sec_analytics',
    items: [
      { icon: 'ri-exchange-dollar-line', label: 'nav_an_market', path: '/market/ueex' },
      { icon: 'ri-newspaper-line', label: 'nav_an_news', path: '/info', badge: 'AI', badgeType: 'live' },
    ],
  },
  {
    title: 'nav_reports',
    items: [
      { icon: 'ri-government-line', label: 'nav_rep_regulatory', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-briefcase-line', label: 'nav_rep_management', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-clipboard-line', label: 'nav_rep_dispatch', locked: true, badge: 'скоро', badgeType: 'new' },
    ],
  },
  {
    title: 'nav_sec_finance',
    items: [
      { icon: 'ri-money-dollar-circle-line', label: 'nav_fin_income', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-calculator-line', label: 'nav_fin_charges', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-battery-share-line', label: 'nav_fin_bess_arb', locked: true, badge: 'скоро', badgeType: 'new' },
    ],
  },
  {
    title: 'nav_documents',
    items: [
      { icon: 'ri-file-list-3-line', label: 'nav_doc_contracts', path: '/contracts' },
      { icon: 'ri-file-paper-2-line', label: 'nav_doc_acts', locked: true, badge: 'скоро', badgeType: 'new' },
      { icon: 'ri-mail-line', label: 'nav_doc_correspondence', locked: true, badge: 'скоро', badgeType: 'new' },
    ],
  },
  {
    title: 'nav_settings',
    items: [
      { icon: 'ri-user-settings-line', label: 'nav_set_users', path: '/admin/users' },
      { icon: 'ri-shield-keyhole-line', label: 'nav_set_roles', path: '/admin/roles' },
      { icon: 'ri-plug-line', label: 'nav_set_integrations', path: '/settings' },
    ],
  },
];


interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
  isMobile: boolean;
  language?: string;
}

export default function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose, isMobile }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const { user } = useAuth();

  const displayName = user?.displayName ?? 'Користувач';
  const role = user ? `${user.role} · ${user.company}` : '';
  const avatarLetters = user?.avatar ?? '?';

  // Owner (CEO) -> trimmed owner menu; all other roles -> full menu (per Меню_*.docx).
  const sections = user?.role === 'CEO' ? OWNER_NAV : FULL_NAV;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getBadgeStyle = (type?: string) => {
    switch (type) {
      case 'new': return 'bg-amber-500/20 text-amber-400 border border-amber-500/30';
      case 'trial': return 'bg-blue-500/20 text-blue-400 border border-blue-500/30';
      case 'live': return 'bg-green-500/20 text-green-400 border border-green-500/30';
      case 'hot': return 'bg-red-500/20 text-[#D12229] border border-red-500/30';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const isActive = (path?: string) => path && location.pathname === path;

  const handleNav = (path?: string) => {
    if (!path) return;
    navigate(path);
    if (isMobile) onMobileClose();
  };

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className="fixed left-0 top-0 h-screen flex flex-col z-30 transition-all duration-300 hidden md:flex"
        style={{
          width: collapsed ? '60px' : '220px',
          background: '#1A1A1A',
          borderRight: '1px solid rgba(255,255,255,0.07)',
        }}
      >
        {/* Logo */}
        <div
          className="flex items-center justify-center h-[72px] shrink-0 relative px-3"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
        >
          {collapsed ? (
            <img
              src="/helios-logo-white.png"
              alt="Helios AG"
              className="h-12 w-auto object-contain"
            />
          ) : (
            <img
              src="/helios-logo-white.png"
              alt="Helios AG"
              className="h-12 w-auto object-contain"
            />
          )}
          <button
            onClick={onToggle}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded cursor-pointer transition-colors"
            style={{ color: '#6B6B70' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#6B6B70'; }}
          >
            <i className={`${collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'} text-base`}></i>
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 scrollbar-thin" style={{ scrollbarWidth: 'none' }}>
          {sections.map((section, si) => (
            <div key={si} className={si > 0 ? 'mt-1' : ''}>
              {section.title && !collapsed && (
                <div className="px-4 py-2 text-[10px] font-semibold tracking-widest uppercase" style={{ color: '#4A4A50' }}>
                  {t(section.title as string)}
                </div>
              )}
              {section.title && collapsed && <div className="my-1 mx-3" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}></div>}
              {section.items.map((item) => {
                const active = isActive(item.path);
                return (
                  <button
                    key={item.label}
                    onClick={() => handleNav(item.path)}
                    onMouseEnter={() => setHoveredItem(item.label)}
                    onMouseLeave={() => setHoveredItem(null)}
                    disabled={item.locked}
                    className="w-full flex items-center text-left transition-all duration-150 cursor-pointer relative group"
                    style={{
                      padding: collapsed ? '8px 0' : '7px 12px 7px 14px',
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      borderLeft: active ? '3px solid #D12229' : '3px solid transparent',
                      background: active
                        ? 'rgba(209,34,41,0.12)'
                        : hoveredItem === item.label && !item.locked
                        ? 'rgba(255,255,255,0.05)'
                        : 'transparent',
                      opacity: item.locked ? 0.45 : 1,
                    }}
                  >
                    <div className="w-4 h-4 flex items-center justify-center shrink-0">
                      <i
                        className={`${item.icon} text-base`}
                        style={{ color: active ? '#D12229' : item.locked ? '#4A4A50' : '#A0A0A8' }}
                      ></i>
                    </div>
                    {!collapsed && (
                      <>
                        <span
                          className="ml-3 text-sm flex-1 whitespace-nowrap"
                          style={{
                            color: active ? '#fff' : item.locked ? '#4A4A50' : '#C0C0C8',
                            fontWeight: active ? 500 : 400,
                            fontFamily: 'Inter, sans-serif',
                          }}
                        >
                          {t(item.label as string)}
                        </span>
                        {item.count && item.count > 0 && (
                          <span className="ml-2 text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" style={{ background: '#D12229', color: '#fff' }}>
                            {item.count}
                          </span>
                        )}
                        {item.locked && (
                          <i className="ri-lock-line text-xs ml-2" style={{ color: '#4A4A50' }}></i>
                        )}
                      </>
                    )}
                    {collapsed && item.count && item.count > 0 && (
                      <span className="absolute top-1 right-1 text-[8px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center" style={{ background: '#D12229', color: '#fff' }}>
                        {item.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Bottom user */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }} className="p-3 shrink-0">
          <div className="flex items-center gap-3 cursor-pointer rounded-lg p-2 transition-colors hover:bg-white/5">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
              style={{ background: '#D12229' }}
            >{avatarLetters}</div>
            {!collapsed && (
              <div className="overflow-hidden">
                <div className="text-sm font-medium text-white whitespace-nowrap">{displayName}</div>
                <div className="text-[11px] whitespace-nowrap" style={{ color: '#6B6B70' }}>{role}</div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Mobile sidebar overlay panel */}
      <aside
        className="fixed top-0 left-0 h-screen flex flex-col z-30 md:hidden transition-transform duration-300"
        style={{
          width: '260px',
          background: '#1A1A1A',
          borderRight: '1px solid rgba(255,255,255,0.07)',
          transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
        }}
      >
        {/* Mobile header with close */}
        <div
          className="flex items-center justify-between h-[72px] shrink-0 px-4"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
        >
          <img
            src="/helios-logo-white.png"
            alt="Helios AG"
            className="h-12 w-auto object-contain"
          />
          <button
            onClick={onMobileClose}
            className="w-8 h-8 flex items-center justify-center rounded cursor-pointer"
            style={{ color: '#A0A0A8' }}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Mobile nav */}
        <nav className="flex-1 overflow-y-auto py-3" style={{ scrollbarWidth: 'none' }}>
          {sections.map((section, si) => (
            <div key={si} className={si > 0 ? 'mt-1' : ''}>
              {section.title && (
                <div className="px-4 py-2 text-[10px] font-semibold tracking-widest uppercase" style={{ color: '#4A4A50' }}>
                  {t(section.title as string)}
                </div>
              )}
              {section.items.map((item) => {
                const active = isActive(item.path);
                return (
                  <button
                    key={item.label}
                    onClick={() => handleNav(item.path)}
                    disabled={item.locked}
                    className="w-full flex items-center text-left transition-all duration-150 cursor-pointer relative"
                    style={{
                      padding: '10px 16px',
                      borderLeft: active ? '3px solid #D12229' : '3px solid transparent',
                      background: active ? 'rgba(209,34,41,0.12)' : 'transparent',
                      opacity: item.locked ? 0.45 : 1,
                    }}
                  >
                    <div className="w-5 h-5 flex items-center justify-center shrink-0">
                      <i
                        className={`${item.icon} text-lg`}
                        style={{ color: active ? '#D12229' : item.locked ? '#4A4A50' : '#A0A0A8' }}
                      ></i>
                    </div>
                    <span
                      className="ml-3 text-sm flex-1 whitespace-nowrap"
                      style={{
                        color: active ? '#fff' : item.locked ? '#4A4A50' : '#C0C0C8',
                        fontWeight: active ? 500 : 400,
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      {t(item.label as string)}
                    </span>
                    {item.count && item.count > 0 && (
                      <span className="ml-2 text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" style={{ background: '#D12229', color: '#fff' }}>
                        {item.count}
                      </span>
                    )}
                    {item.locked && (
                      <i className="ri-lock-line text-xs ml-2" style={{ color: '#4A4A50' }}></i>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Mobile bottom user */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }} className="p-4 shrink-0">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
              style={{ background: '#D12229' }}
            >{avatarLetters}</div>
            <div className="overflow-hidden">
              <div className="text-sm font-medium text-white whitespace-nowrap">{displayName}</div>
              <div className="text-[11px] whitespace-nowrap" style={{ color: '#6B6B70' }}>{role}</div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}